import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Manual email dialog. Recipient email is looked up SERVER-SIDE from the
 * customer_id — this dialog never sends a recipient address up. The user
 * may only override the subject and body text; HTML is generated on the
 * backend inside a fixed shell.
 */
export default function SendEmailDialog({
  open, onOpenChange,
  customerId, customerName, customerEmail,
  vehicleId, contractId,
  onSent,
}) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [attachPdf, setAttachPdf] = useState(false);
  const [pdfMode, setPdfMode] = useState("contract");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/email-templates");
        setTemplates(data || []);
        // Default: HU reminder if in HU context (vehicle passed and template exists),
        // otherwise the first custom template.
        const hu = (data || []).find((t) => t.kind === "hu_reminder");
        const first = (data || [])[0];
        const t0 = vehicleId && hu ? hu : first;
        if (t0) {
          setTemplateId(t0.id);
          setSubject(t0.subject || "");
          setBody(t0.body || "");
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error");
      }
    })();
  }, [open, vehicleId]);

  const onTemplateChange = (id) => {
    setTemplateId(id);
    const tpl = templates.find((t) => t.id === id);
    if (tpl) {
      setSubject(tpl.subject || "");
      setBody(tpl.body || "");
    }
  };

  const send = async () => {
    if (!customerEmail) {
      toast.error(t("emails.no_email"));
      return;
    }
    setSending(true);
    try {
      await api.post("/emails/send", {
        template_id: templateId,
        customer_id: customerId,
        vehicle_id: vehicleId || null,
        contract_id: contractId || null,
        subject_override: subject,
        body_override: body,
        attach_contract_pdf: !!contractId && attachPdf,
        pdf_mode: pdfMode,
      });
      toast.success(t("emails.sent"));
      onOpenChange(false);
      onSent && onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#21252E] border-[#2E3241] text-white max-w-2xl"
        aria-describedby="send-email-desc"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-400" /> {t("emails.send")}
          </DialogTitle>
        </DialogHeader>

        <div id="send-email-desc" className="text-xs text-zinc-500 -mt-2 mb-1">
          {t("emails.preview_note")}
        </div>

        <div className="space-y-3">
          <div className="text-sm text-zinc-300">
            <span className="text-zinc-500">{t("emails.log_recipient")}:</span>{" "}
            <span className="font-medium">{customerName}</span>{" "}
            <span className="text-zinc-500">&lt;{customerEmail || "—"}&gt;</span>
          </div>

          <div>
            <Label className="text-xs">{t("emails.template")}</Label>
            <Select value={templateId} onValueChange={onTemplateChange}>
              <SelectTrigger className="bg-[#171B24] border-[#2E3241]" data-testid="email-template-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">{t("emails.subject")}</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-[#171B24] border-[#2E3241]"
              data-testid="email-subject"
            />
          </div>

          <div>
            <Label className="text-xs">{t("emails.body")}</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="bg-[#171B24] border-[#2E3241] font-mono text-sm"
              data-testid="email-body"
            />
            <div className="text-[11px] text-zinc-500 mt-1">
              {t("emails.placeholders_hint")}
            </div>
          </div>

          {contractId && (
            <div className="rounded-lg border border-[#2E3241] bg-[#171B24] p-3 space-y-2" data-testid="attach-pdf-block">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-blue-500"
                  checked={attachPdf}
                  onChange={(e) => setAttachPdf(e.target.checked)}
                  data-testid="attach-pdf-checkbox"
                />
                <span className="text-sm text-zinc-200">{t("emails.attach_pdf")}</span>
              </label>
              {attachPdf && (
                <div className="pl-6 flex items-center gap-4 text-sm text-zinc-300">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="pdfMode"
                      value="contract"
                      checked={pdfMode === "contract"}
                      onChange={() => setPdfMode("contract")}
                      className="accent-blue-500"
                      data-testid="pdf-mode-contract"
                    />
                    {t("emails.pdf_mode_contract")}
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="pdfMode"
                      value="invoice"
                      checked={pdfMode === "invoice"}
                      onChange={() => setPdfMode("invoice")}
                      className="accent-blue-500"
                      data-testid="pdf-mode-invoice"
                    />
                    {t("emails.pdf_mode_invoice")}
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={send}
            disabled={sending || !customerEmail || !templateId}
            className="bg-blue-500 hover:bg-blue-600"
            data-testid="email-send-btn"
          >
            <Send className="h-4 w-4 mr-1" /> {t("emails.send_short")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
